import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _Object$entries from 'babel-runtime/core-js/object/entries';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _Object$values from 'babel-runtime/core-js/object/values';
import _typeof from 'babel-runtime/helpers/typeof';
import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _extends from 'babel-runtime/helpers/extends';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import invariant from '../../utils/invariant';
import wrapDisplayName from '../../utils/wrapDisplayName';
import shallowEqual from '../../utils/shallowEqual';
import identity from '../../fp/identity';
import Render from '../Render';

/**
 * @param {function} setState
 * @returns {function}
 */
export function defaultChangeEffect(setState) {
  return setState;
}

/**
 * @param {object} stateProps
 * @param {Object<string, function>} effectProps
 * @param {object} ownProps
 */
export function defaultMergeProps(stateProps, effectProps, ownProps) {
  return _extends({}, ownProps, stateProps, effectProps);
}

/**
 * @param {object} options
 * @param {string} options.displayName
 * @param {function} [options.selector=identity]
 * @param {Object<string, function>|function:object} options.effects
 * @param {function:object} [options.mergeProps=defaultMergeProps]
 * @param {object|function} [options.initialState={}]
 * @returns {State}
 */
function connectState() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return function (component) {
    invariant(typeof component === 'string' || typeof component === 'function', 'component expected a string or a function, instead received %s.', component);

    var _options$displayName = options.displayName,
        displayName = _options$displayName === undefined ? wrapDisplayName(component, 'State') : _options$displayName,
        _options$effects = options.effects,
        effects = _options$effects === undefined ? {
      onChange: defaultChangeEffect
    } : _options$effects,
        _options$selector = options.selector,
        selector = _options$selector === undefined ? identity : _options$selector,
        _options$initialState = options.initialState,
        initialState = _options$initialState === undefined ? {} : _options$initialState,
        _options$mergeProps = options.mergeProps,
        mergeProps = _options$mergeProps === undefined ? defaultMergeProps : _options$mergeProps,
        ownProps = _objectWithoutProperties(options, ['displayName', 'effects', 'selector', 'initialState', 'mergeProps']);

    invariant(typeof effects === 'function' || (typeof effects === 'undefined' ? 'undefined' : _typeof(effects)) === 'object' && effects !== null && _Object$values(effects).every(function (e) {
      return typeof e === 'function';
    }), 'options.effects expected a function or a object that every props is a function, instead received %s.', effects);

    invariant(typeof selector === 'function', 'options.selector expected a function, instead received %s.', selector);

    var State = function (_Component) {
      _inherits(State, _Component);

      /**
       * @constructor
       * @param {object} props
       * @param {object} context
       * @returns {State}
       */
      function State(props, context) {
        _classCallCheck(this, State);

        var _this = _possibleConstructorReturn(this, (State.__proto__ || _Object$getPrototypeOf(State)).call(this, props, context));

        var initialState = props.initialState;


        var state = typeof initialState === 'function' ? initialState(props) : initialState;
        _this.state = state;

        _this.updateEffectProps();
        _this.updateStateProps(props, state);
        _this.updateOwnProps(props);
        _this.updateMergedProps(props);
        return _this;
      }

      /**
       * @public
       * @returns {void}
       */

      /**
       * @static
       * @var {object}
       */


      _createClass(State, [{
        key: 'componentWillUpdate',
        value: function componentWillUpdate(nextProps, nextState) {
          var props = this.props,
              state = this.state,
              stateProps = this.stateProps,
              ownProps = this.ownProps;


          var nextStateProps = stateProps;
          var nextOwnProps = ownProps;
          var propsEqual = shallowEqual(nextProps, props);
          var stateEqual = shallowEqual(nextState, state);

          if (!propsEqual) {
            nextOwnProps = this.updateOwnProps(nextProps);
          }

          if (!propsEqual || !stateEqual) {
            nextStateProps = this.updateStateProps(nextProps, nextState);
          }

          if (!shallowEqual(nextOwnProps, ownProps) || !shallowEqual(nextStateProps, stateProps)) {
            this.updateMergedProps(nextProps);
          }
        }

        /**
         * @public
         * @param {object} props
         * @param {object} state
         * @returns {object}
         */

      }, {
        key: 'updateStateProps',
        value: function updateStateProps(props, state) {
          var stateProps = selector(state, props);

          invariant((typeof stateProps === 'undefined' ? 'undefined' : _typeof(stateProps)) === 'object' && stateProps !== null, 'selector expected a function return a non-null object, instead returned %s.', stateProps);

          this.stateProps = stateProps;

          return stateProps;
        }

        /**
         * @public
         * @returns {Object<string, function>}
         */

      }, {
        key: 'updateEffectProps',
        value: function updateEffectProps() {
          var _this2 = this;

          var effectProps = void 0;
          var setState = function setState() {
            return _this2.setState.apply(_this2, arguments);
          };
          var getState = function getState() {
            return _this2.state;
          };

          if (typeof effects === 'function') {
            effectProps = effects(setState, getState);

            invariant((typeof effectProps === 'undefined' ? 'undefined' : _typeof(effectProps)) === 'object' && effectProps !== null && _Object$values(effectProps).every(function (e) {
              return typeof e === 'function';
            }), 'effects expected a function return a object that every props is a function, instead returned %s.', effectProps);
          } else {
            effectProps = _Object$entries(effects).reduce(function (acc, _ref) {
              var _ref2 = _slicedToArray(_ref, 2),
                  name = _ref2[0],
                  effect = _ref2[1];

              var updater = effect(setState, getState);

              invariant(typeof updater === 'function', '%s effect expected a function return a function, instead returned %s.', name, updater);

              acc[name] = updater;

              return acc;
            }, {});
          }

          this.effectProps = effectProps;

          return effectProps;
        }

        /**
         * @public
         * @param {object} props
         * @returns {object}
         */

      }, {
        key: 'updateOwnProps',
        value: function updateOwnProps(props) {
          var mergeProps = props.mergeProps,
              initialState = props.initialState,
              innerRef = props.innerRef,
              ownProps = _objectWithoutProperties(props, ['mergeProps', 'initialState', 'innerRef']);

          this.ownProps = ownProps;

          return ownProps;
        }

        /**
         * @public
         * @param {object} props
         * @returns {object}
         */

      }, {
        key: 'updateMergedProps',
        value: function updateMergedProps(props) {
          var mergeProps = props.mergeProps;
          var stateProps = this.stateProps,
              effectProps = this.effectProps,
              ownProps = this.ownProps;


          var mergedProps = mergeProps(stateProps, effectProps, ownProps);
          this.mergedProps = mergedProps;

          return mergedProps;
        }

        /**
         * @public
         * @returns {object}
         */

      }, {
        key: 'render',
        value: function render() {
          var innerRef = this.props.innerRef,
              mergedProps = this.mergedProps;


          var Component = component;

          return React.createElement(Component, _extends({}, mergedProps, {
            ref: innerRef
          }));
        }
      }]);

      return State;
    }(Component);

    State.defaultProps = _extends({}, ownProps, {
      initialState: initialState,
      mergeProps: mergeProps });


    if (process.env.NODE_ENV !== 'production') {
      State.displayName = displayName;
      State.propTypes = {
        innerRef: PropTypes.func,
        mergeProps: PropTypes.func.isRequired,
        initialState: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired
      };
    }

    return State;
  };
}

export var State = connectState({
  displayName: 'State'
})(Render);

export default connectState;


//////////////////
// WEBPACK FOOTER
// ./node_modules/lyjs/es/components/connectState/connectState.js
// module id = 757
// module chunks = 0